import { useEffect, useMemo, useState } from "react";
import { enterStaffPortal, applyStaffPortalDocument } from "@/lib/staffPortal";
import { readStoredDriverLang, translateDriverString } from "@/lib/driverI18n";
import { hasPartnerSession } from "@/lib/driverPartner";

const CONFIG = {
  kitchen: {
    title: "Kitchen app",
    subtitle: "Baker · Froster · Decorator",
    accent: "#EE2677",
    bg: "#F5F2EA",
    continueLabel: "Open Kitchen",
    sessionKey: "ttt_kitchen_continued",
    hubErrorPath: "/pp/hub",
  },
  driver: {
    title: "Delivery app",
    subtitle: "Driver dashboard",
    accent: "#EE2677",
    bg: "#F5F2EA",
    continueLabel: "Open Delivery",
    sessionKey: "ttt_driver_continued",
    hubErrorPath: "/dd/hub",
  },
};

function isStandaloneDisplay() {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
}

/** New delivery partners must sign up first; returning partners go to the hub. */
function driverPortalEntryUrl() {
  if (hasPartnerSession()) {
    return `${window.location.origin}/dd/hub`;
  }
  return `${window.location.origin}/dd/join`;
}

export default function StaffPortalInstallGate({ kind, code, fetchPath, shortHomePath }) {
  const cfg = CONFIG[kind];
  const [redirectUrl, setRedirectUrl] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [continuing, setContinuing] = useState(false);
  const standalone = useMemo(isStandaloneDisplay, []);

  useEffect(() => {
    if (!code || !cfg) return;

    const shortHome = `${window.location.origin}${shortHomePath}`;
    enterStaffPortal(kind, shortHome);
    applyStaffPortalDocument(kind);

    let cancelled = false;
    fetch(fetchPath)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (r.ok && data.redirect_url) {
          setRedirectUrl(data.redirect_url);
          return;
        }
        setLoadErr("invalid");
      })
      .catch(() => {
        if (!cancelled) setLoadErr("fetch");
      });

    return () => {
      cancelled = true;
    };
  }, [kind, code, cfg, fetchPath, shortHomePath]);

  useEffect(() => {
    if (!redirectUrl || loadErr) return;
    if (!standalone && !sessionStorage.getItem(cfg.sessionKey)) return;
    const target = kind === "driver" ? driverPortalEntryUrl() : redirectUrl;
    window.location.replace(target);
  }, [redirectUrl, loadErr, standalone, cfg.sessionKey, kind]);

  useEffect(() => {
    if (!loadErr) return;
    const fallback =
      kind === "driver" && !hasPartnerSession()
        ? `${window.location.origin}/dd/join`
        : `${cfg.hubErrorPath}?error=${loadErr}`;
    window.location.replace(fallback);
  }, [loadErr, cfg.hubErrorPath, kind]);

  const onContinue = () => {
    if (!redirectUrl) {
      setContinuing(true);
      return;
    }
    sessionStorage.setItem(cfg.sessionKey, "1");
    window.location.href = kind === "driver" ? driverPortalEntryUrl() : redirectUrl;
  };

  if (loadErr) {
    return <div style={{ minHeight: "100dvh", background: cfg.bg }} />;
  }

  const waiting = continuing && !redirectUrl;
  const lang = kind === "driver" ? readStoredDriverLang() : "en";
  const dt = (key) => (kind === "driver" ? translateDriverString(lang, key) : null);

  const title =
    kind === "driver" ? dt("installTitle") || cfg.title : cfg.title;
  const subtitle =
    kind === "driver" ? dt("installSubtitle") || cfg.subtitle : cfg.subtitle;
  const continueLabel =
    kind === "driver" ? dt("installContinue") || cfg.continueLabel : cfg.continueLabel;
  const loadingLabel = kind === "driver" ? dt("loading") || "Loading…" : "Loading…";

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: cfg.bg,
        fontFamily: "Inter, system-ui, sans-serif",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#fff",
          borderRadius: 20,
          border: `2px solid ${cfg.accent}22`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          padding: "28px 24px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 64,
            height: 64,
            margin: "0 auto 16px",
            borderRadius: 16,
            background: `${cfg.accent}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
          }}
        >
          {kind === "kitchen" ? "🎂" : "🚚"}
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#2D2D2D" }}>{title}</h1>
        <p style={{ margin: "8px 0 20px", color: "#666", fontSize: 14 }}>{subtitle}</p>

        {!standalone ? (
          <div
            style={{
              textAlign: "left",
              background: cfg.bg,
              borderRadius: 12,
              padding: "14px 16px",
              marginBottom: 20,
              fontSize: 13,
              lineHeight: 1.55,
              color: "#444",
            }}
          >
            <strong style={{ display: "block", marginBottom: 6, color: "#2D2D2D" }}>
              {kind === "driver"
                ? dt("installAddHomeTitle")
                : "Add to home screen (separate from the other app):"}
            </strong>
            {kind === "driver" ? (
              <>
                {dt("installStep1")}
                <br />
                {dt("installStep2")}
                <br />
                {dt("installStep3Name")}
              </>
            ) : (
              <>
                1. Stay on this page
                <br />
                2. Chrome menu (⋮) → <strong>Install app</strong> or <strong>Add to Home screen</strong>
                <br />
                3. Name it <strong>{kind === "kitchen" ? "Kitchen" : "Delivery"}</strong>
              </>
            )}
          </div>
        ) : null}

        <button
          type="button"
          onClick={onContinue}
          disabled={waiting}
          style={{
            width: "100%",
            border: "none",
            borderRadius: 14,
            padding: "14px 16px",
            fontSize: 16,
            fontWeight: 700,
            color: "#fff",
            background: cfg.accent,
            cursor: waiting ? "wait" : "pointer",
            opacity: waiting ? 0.7 : 1,
          }}
        >
          {waiting ? loadingLabel : continueLabel}
        </button>
      </div>
    </div>
  );
}
